import * as tabAccessRules from '@/rules/tab-access'
import { asoIntelModule } from '@/rules/page-access'
import { asoReportGuard, maAuthenticationGuard, maAuthorizationGuard, maCountryGuard, maDateRangeGuard, maParamsQueryNormalizeGuard } from '@/router/guards'
import { Param, Query, RouteName, TitleFont } from '@/base/constants/route'
import { PRODUCT_FAMILY, PRODUCTS } from '@/base/constants/authorization'

const asoRoutes = {
  path: 'aso-intelligence',
  name: RouteName.ASO_INTELLIGENCE,
  meta: {
    productFamily: PRODUCT_FAMILY.ASO,
    shortTitle: 'ASO',
    icon: 'radar',
    parent: true,
    requiredProducts: [PRODUCTS.ASO_LITE, PRODUCTS.ASO_BASIC_V2, PRODUCTS.ASO_PRO, PRODUCTS.ASO_BUSINESS],
  },
  redirect: { name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_TRACKING },
  component: () => import('@/layouts/Dashboard.vue'),
  beforeEnter: maAuthenticationGuard,
  children: [
    {
      path: 'metadata',
      name: RouteName.ASO_INTELLIGENCE_METADATA,
      meta: {
        title: 'METADATA',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_TRACKING },
      children: [
        {
          path: 'aso-overview/:trackId?',
          name: RouteName.ASO_INTELLIGENCE_METADATA_ASO_OVERVIEW,
          meta: {
            title: 'ASO Overview',
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_CODE],
            analyticsPageName: 'ASO Overview',
            productFamily: PRODUCT_FAMILY.ASO,
            sub: true,
            requiredAccess: asoIntelModule.asoOverviewAccessRule,
          },
          component: () => import('@/pages/aso-overview/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'metadata-optimizer/:trackId?',
          name: RouteName.ASO_INTELLIGENCE_METADATA_METADATA_OPTIMIZER,
          meta: {
            title: 'Metadata Optimizer',
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_CODE, Query.DEVICE],
            analyticsPageName: 'Metadata Optimizer',
            productFamily: PRODUCT_FAMILY.ASO,
            requiredAccess: asoIntelModule.keywordOptimizationAccessRule,
          },
          component: () => import('@/pages/keyword-optimization/KeywordOptimization.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'app-update-timeline/:trackId?',
          name: RouteName.ASO_INTELLIGENCE_METADATA_APP_UPDATE_TIMELINE,
          meta: {
            title: 'App Update Timeline',
            analyticsPageName: 'App Update Timeline',
            disabledAutoScroll: true,
            productFamily: PRODUCT_FAMILY.ASO,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [
              Query.ACTIVE_APP,
              Query.COUNTRY_CODE,
              Query.START_DATE,
              Query.END_DATE,
            ],
            requiredAccess: asoIntelModule.appUpdateTimelineAccessRule,
          },
          component: () => import('@/pages/app-update-timeline/AppUpdateTimeline.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
        },
        {
          path: 'creative-monitoring',
          name: RouteName.ASO_INTELLIGENCE_METADATA_CREATIVE_MONITORING,
          meta: {
            title: 'Creative Monitoring',
            analyticsPageName: 'Creative Monitoring',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            requiredAccess: asoIntelModule.creativeAssetHubAccessRule,
          },
          component: () => import('@/pages/assets-analysis/index.vue'),
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.ASO_INTELLIGENCE_METADATA_CREATIVE_MONITORING_CREATIVES_BY_CATEGORY_TAB },
          children: [
            {
              path: 'creatives-by-category/:trackId?',
              name: RouteName.ASO_INTELLIGENCE_METADATA_CREATIVE_MONITORING_CREATIVES_BY_CATEGORY_TAB,
              meta: {
                title: 'Creative Monitoring',
                analyticsPageName: 'Creative Monitoring - Creatives By Category',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE, Query.CATEGORY_ID, Query.DATE, Query.DEVICE, Query.VIDEO_FILTER],
              },
              components: {
                tab: () => import('@/pages/assets-analysis/CreativesByCategory.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'creatives-by-app/:trackId?',
              name: RouteName.ASO_INTELLIGENCE_METADATA_CREATIVE_MONITORING_CREATIVES_BY_APP_TAB,
              meta: {
                title: 'Creative Monitoring',
                analyticsPageName: 'Creative Monitoring - Creatives By App',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE, Query.CATEGORY_ID, Query.DATE, Query.VIDEO_FILTER],
              },
              components: {
                tab: () => import('@/pages/assets-analysis/CreativesByApp.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'localization/:trackId?',
          name: RouteName.ASO_INTELLIGENCE_METADATA_LOCALIZATION,
          meta: {
            title: 'Localization',
            analyticsPageName: 'Localization',
            productFamily: PRODUCT_FAMILY.ASO,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [
              Query.COUNTRY_CODE,
            ],
            requiredAccess: asoIntelModule.localizationAccessRule,
          },
          component: () => import('@/pages/localization/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'store-management/:trackId?',
          name: RouteName.ASO_INTELLIGENCE_METADATA_STORE_MANAGEMENT,
          meta: {
            title: 'Store Management',
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_CODE, Query.LANGUAGE_CODE],
            analyticsPageName: 'Store Management',
            productFamily: PRODUCT_FAMILY.ASO,
            requiredAccess: asoIntelModule.storeManagementAccessRule,
            sub: true,
          },
          component: () => import('@/pages/store-management/StoreManagement.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
      ],
    },
    {
      path: 'keyword',
      name: RouteName.ASO_INTELLIGENCE_KEYWORD,
      meta: {
        title: 'KEYWORD',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_TRACKING },
      children: [
        {
          path: 'keyword-tracking/:trackId?',
          name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_TRACKING,
          meta: {
            title: 'Keyword Tracking',
            analyticsPageName: 'Keyword Tracking',
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_CODE, Query.DEVICE],
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            requiredAccess: asoIntelModule.keywordTrackingAccessRule,
          },
          component: () => import('@/pages/keyword-tracking/index.vue'),
          redirect: { name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_TRACKING_TRACKED_KEYWORDS_TAB },
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
          children: [
            {
              path: '',
              name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_TRACKING_TRACKED_KEYWORDS_TAB,
              meta: {
                title: 'Keyword Tracking',
                analyticsPageName: 'Keyword Tracking - Tracked Keywords',
                productFamily: PRODUCT_FAMILY.ASO,
                ignoredQueries: [Query.START_DATE, Query.END_DATE, Query.DATE],
              },
              component: () => import('@/pages/keyword-tracking/tracked-keywords/index.vue'),
            },
            {
              path: 'competitor-analysis',
              name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_TRACKING_COMPETITOR_ANALYSIS_TAB,
              meta: {
                title: 'Keyword Tracking',
                analyticsPageName: 'Keyword Tracking - Competitor Analysis',
                productFamily: PRODUCT_FAMILY.ASO,
                requiredQueries: [Query.DATE],
                ignoredQueries: [Query.START_DATE, Query.END_DATE],
              },
              component: () => import('@/pages/keyword-tracking/competitor-analysis/index.vue'),
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard],
              maCountryGuard,
            },
            {
              path: 'ranking-history',
              name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_TRACKING_RANKING_HISTORY_TAB,
              meta: {
                title: 'Keyword Tracking',
                analyticsPageName: 'Keyword Tracking - Ranking History',
                productFamily: PRODUCT_FAMILY.ASO,
                requiredQueries: [Query.START_DATE, Query.END_DATE],
              },
              component: () => import('@/pages/keyword-tracking/ranking-history/index.vue'),
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'competitor-keywords',
          name: RouteName.ASO_INTELLIGENCE_KEYWORD_COMPETITOR_KEYWORDS,
          meta: {
            title: 'Competitor Keywords',
            analyticsPageName: 'Competitor Keywords',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            requiredAccess: asoIntelModule.competitorKeywordsAccessRule,
          },
          component: () => import('@/pages/competitor-keywords/index.vue'),
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.ASO_INTELLIGENCE_KEYWORD_COMPETITOR_KEYWORDS_DIRECT_COMPARISON_TAB },
          children: [
            {
              path: 'direct-comparison/:trackId?',
              name: RouteName.ASO_INTELLIGENCE_KEYWORD_COMPETITOR_KEYWORDS_DIRECT_COMPARISON_TAB,
              meta: {
                title: 'Competitor Keywords',
                analyticsPageName: 'Competitor Keywords - Direct Comparison',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE],
              },
              components: {
                tab: () => import('@/pages/competitor-keywords/direct-comparison/index.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
            {
              path: 'multi-comparison/:trackId?',
              name: RouteName.ASO_INTELLIGENCE_KEYWORD_COMPETITOR_KEYWORDS_MULTI_COMPARISON_TAB,
              meta: {
                title: 'Competitor Keywords',
                analyticsPageName: 'Competitor Keywords - Multi Comparison',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE],
                requiredAccess: tabAccessRules.accessCompetitorKeywordsMultiComparisonTabRule,
              },
              components: {
                tab: () => import('@/pages/competitor-keywords/multi-comparison/index.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'keyword-inspector',
          name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_INSPECTOR,
          meta: {
            title: 'Keyword Inspector',
            analyticsPageName: 'Keyword Inspector',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            requiredAccess: asoIntelModule.keywordInspectorAccessRule,
          },
          component: () => import('@/pages/keyword-inspector/KeywordInspector.vue'),
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_INSPECTOR_KEYWORD_ANALYSIS },
          children: [
            {
              path: 'keyword-analysis',
              name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_INSPECTOR_KEYWORD_ANALYSIS,
              meta: {
                title: 'Keyword Inspector',
                analyticsPageName: 'Keyword Inspector - Keyword Analysis',
                requiredQueries: [Query.STORE, Query.COUNTRY_FILTER, Query.KEYWORD],
                ignoredQueries: [Query.KEYWORDS],
              },
              components: {
                tab: () => import('@/pages/keyword-inspector/keyword-analysis/KeywordAnalysis.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
            {
              path: 'performance-comparison',
              name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_INSPECTOR_PERFORMANCE_COMPARISON,
              meta: {
                title: 'Keyword Inspector',
                analyticsPageName: 'Keyword Inspector - Performance Comparison',
                requiredQueries: [Query.STORE, Query.COUNTRY_FILTER, Query.KEYWORDS],
                ignoredQueries: [Query.KEYWORD],
                requiredAccess: tabAccessRules.accessKeywordInspectorPerformanceComparisonTabRule,
              },
              components: {
                tab: () => import('@/pages/keyword-inspector/performance-comparison/PerformanceComparison.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'keyword-trends/:trackId?',
          name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_TRENDS,
          meta: {
            title: 'Keyword Trends',
            analyticsPageName: 'Keyword Trends',
            productFamily: PRODUCT_FAMILY.ASO,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_FILTER, Query.CATEGORY_ID],
            requiredAccess: asoIntelModule.keywordTrendsAccessRule,
          },
          component: () => import('@/pages/keyword-trends/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'keyword-translator/:trackId?',
          name: RouteName.ASO_INTELLIGENCE_KEYWORD_KEYWORD_TRANSLATOR,
          meta: {
            title: 'Keyword Translator',
            analyticsPageName: 'Keyword Translator',
            productFamily: PRODUCT_FAMILY.ASO,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.LANGUAGE_CODE],
            requiredAccess: asoIntelModule.keywordTranslatorAccessRule,
          },
          component: () => import('@/pages/keyword-translator/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
      ],
    },
    {
      path: 'performance',
      name: RouteName.ASO_INTELLIGENCE_PERFORMANCE,
      meta: {
        title: 'PERFORMANCE',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_ASO_REPORT },
      children: [
        {
          path: 'download-report',
          name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_DOWNLOAD_REPORT,
          meta: {
            title: 'iOS Download Report',
            analyticsPageName: 'iOS Download Report',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            disabledAutoScroll: true,
            navigationWithoutAppTrack: true,
            hideGlobalSearch: true,
          },
          component: () => import('@/pages/download-report/index.vue'),
          beforeEnter: [maAuthorizationGuard, asoReportGuard],
          children: [
            {
              path: 'integration',
              name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_DOWNLOAD_REPORT_INTEGRATION,
              meta: {
                title: 'iOS Download Report',
                analyticsPageName: 'iOS Download Report - Integration',
                productFamily: PRODUCT_FAMILY.ASO,
                disabledAutoScroll: true,
                requiredAccess: asoIntelModule.downloadReportAccessRule,
                navigationWithoutAppTrack: true,
                hideGlobalSearch: true,
              },
              component: () => import('@/pages/download-report/integration/index.vue'),
            },
            {
              path: 'dashboard',
              name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_DOWNLOAD_REPORT_DASHBOARD,
              meta: {
                title: 'iOS Download Report',
                requiredQueries: [
                  Query.START_DATE,
                  Query.END_DATE,
                ],
                analyticsPageName: 'iOS Download Report - Dashboard',
                productFamily: PRODUCT_FAMILY.ASO,
                disabledAutoScroll: true,
                requiredAccess: tabAccessRules.downloadReportAccessRule,
                navigationWithoutAppTrack: true,
                hideGlobalSearch: true,
              },
              component: () => import('@/pages/download-report/dashboard/index.vue'),
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'aso-report',
          name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_ASO_REPORT,
          meta: {
            title: 'ASO Report',
            analyticsPageName: 'ASO Report',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            disabledAutoScroll: true,
          },
          component: () => import('@/pages/aso-report/index.vue'),
          beforeEnter: [maAuthorizationGuard, asoReportGuard],
          children: [
            {
              path: 'tracked-keywords/:trackId?',
              name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_ASO_REPORT_TRACKED_KEYWORDS,
              meta: {
                title: 'ASO Report',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.COUNTRY_CODE,
                  Query.ACTIVE_APP,
                  Query.START_DATE,
                  Query.DEVICE,
                ],
                analyticsPageName: 'ASO Report - Tracked Keywords',
                productFamily: PRODUCT_FAMILY.ASO,
                disabledAutoScroll: true,
                requiredAccess: asoIntelModule.asoReportAccessRule,
              },
              component: () => import('@/pages/aso-report/tracked-keywords/index.vue'),
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'all-ranked-keywords/:trackId?',
              name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_ASO_REPORT_ALL_RANKED_KEYWORDS,
              meta: {
                title: 'ASO Report',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.COUNTRY_CODE,
                  Query.ACTIVE_APP,
                  Query.START_DATE,
                  Query.DEVICE,
                ],
                analyticsPageName: 'ASO Report - All Ranked Keywords',
                productFamily: PRODUCT_FAMILY.ASO,
                disabledAutoScroll: true,
                requiredAccess: tabAccessRules.accessAsoReportAllRankedKeywordsTabRule,
              },
              component: () => import('@/pages/aso-report/all-ranked-keywords/index.vue'),
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'visibility-report',
          name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_VISIBILITY_REPORT,
          meta: {
            title: 'Visibility Report',
            analyticsPageName: 'Visibility Report',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            requiredAccess: asoIntelModule.visibilityReportAccessRule,
          },
          component: () => import('@/pages/visibility-report/index.vue'),
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_VISIBILITY_REPORT_VISIBILITY_SNAPSHOT_TAB },
          children: [
            {
              path: 'visibility-snapshot/:trackId?',
              name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_VISIBILITY_REPORT_VISIBILITY_SNAPSHOT_TAB,
              meta: {
                title: 'Visibility Report',
                analyticsPageName: 'Visibility Report - Visibility Snapshot',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE, Query.START_DATE, Query.END_DATE],
                disabledAutoScroll: true,
              },
              components: {
                tab: () => import('@/pages/visibility-report/components/VisibilitySnapshot.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
            {
              path: 'localization-performance/:trackId?',
              name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_VISIBILITY_REPORT_LOCALIZATION_PERFORMANCE_TAB,
              meta: {
                title: 'Visibility Report',
                analyticsPageName: 'Visibility Report - Localization Performance',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE],
                disabledAutoScroll: true,
              },
              components: {
                tab: () => import('@/pages/visibility-report/components/LocalizationPerformance.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
            {
              path: 'top-visible-apps/:trackId?',
              name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_VISIBILITY_REPORT_TOP_VISIBLE_APPS_TAB,
              meta: {
                title: 'Visibility Report',
                analyticsPageName: 'Visibility Report - Top Visible Apps',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE, Query.CATEGORY_ID, Query.DATE],
              },
              components: {
                tab: () => import('@/pages/visibility-report/components/TopVisibleApps.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'download-share/:trackId?',
          name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_DOWNLOAD_SHARE,
          meta: {
            title: 'Download Share',
            ignoreChildrenFromMenu: true,
            requiredParams: [Param.TRACK_ID],
            analyticsPageName: 'Download Share',
            productFamily: PRODUCT_FAMILY.ASO,
            disabledAutoScroll: true,
            requiredAccess: asoIntelModule.keywordIntelligenceAccessRule,
          },
          component: () => import('@/pages/keyword-intelligence/KeywordIntelligence.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
          redirect: { name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_DOWNLOAD_SHARE_KEYWORDS_TAB },
          children: [
            {
              path: 'keywords',
              name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_DOWNLOAD_SHARE_KEYWORDS_TAB,
              meta: {
                title: 'Download Share',
                analyticsPageName: 'Download Share - Keywords',
                requiredQueries: [Query.COUNTRY_CODE, Query.ACTIVE_APP, Query.DATE, Query.DEVICE],
              },
              components: {
                tab: () => import('@/pages/keyword-intelligence/MaKeywordsTab.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'organic-download-history',
              name: RouteName.ASO_INTELLIGENCE_PERFORMANCE_DOWNLOAD_SHARE_ORGANIC_DOWNLOAD_HISTORY_TAB,
              meta: {
                title: 'Download Share',
                analyticsPageName: 'Download Share - Organic Download History',
                requiredQueries: [Query.COUNTRY_CODE, Query.ACTIVE_APP, Query.DEVICE],
              },
              components: {
                tab: () => import('@/pages/keyword-intelligence/MaOrganicDownloadHistoryTab.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
          ],
        },
      ],
    },
  ],
}

export default asoRoutes
